import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import InventoryManager from "./admin/InventoryManager";
import InventoryImport from "./admin/InventoryImport";
import PricingSettings from "./admin/PricingSettings";
import AuctionApproval from "./admin/AuctionApproval";
import CertificateGenerator from "./admin/CertificateGenerator";
import OrderTracking from "./admin/OrderTracking";
import CustomerCRM from "./admin/CustomerCRM";
import TranslationsEditor from "./admin/TranslationsEditor";
import FxOverrideEditor from "./admin/FxOverrideEditor";
import VisitorAnalytics from "./admin/VisitorAnalytics";
import BirthstoneZodiacEditor from "./admin/BirthstoneZodiacEditor";
import NotificationsPanel from "./admin/NotificationsPanel";
import QrisOrders from "./admin/QrisOrders";
import ReviewsAdmin from "./admin/ReviewsAdmin";

const Admin = () => {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");
  const [inquiries, setInquiries] = useState([]);
  const [kycSubs, setKycSubs] = useState([]);
  const [riskFlags, setRiskFlags] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogCategory, setBlogCategory] = useState("market_insight");
  const [genBusy, setGenBusy] = useState(false);
  const [passports, setPassports] = useState([]);
  const [mpAnalytics, setMpAnalytics] = useState(null);
  const [cleanup, setCleanup] = useState({ preview: null, phrase: "", wipeUsers: false, wipeBlog: false, busy: false, result: null });

  const loadCleanupPreview = () =>
    api.get("/admin/cleanup/preview")
      .then(({ data }) => setCleanup((c) => ({ ...c, preview: data })))
      .catch(() => {});

  const executeCleanup = async () => {
    if (cleanup.phrase.trim() !== "DELETE TEST DATA") {
      toast.error("Type DELETE TEST DATA exactly to confirm.");
      return;
    }
    setCleanup((c) => ({ ...c, busy: true }));
    try {
      const { data } = await api.post("/admin/cleanup/execute", {
        confirm_phrase: cleanup.phrase,
        wipe_users: cleanup.wipeUsers,
        wipe_blog: cleanup.wipeBlog,
      });
      setCleanup((c) => ({ ...c, busy: false, result: data, phrase: "" }));
      toast.success("Test data wiped.");
      // Refresh counts
      api.get("/admin/stats").then(({ data }) => setStats(data));
      loadCleanupPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup failed");
      setCleanup((c) => ({ ...c, busy: false }));
    }
  };

  const loadKyc = () => api.get("/admin/kyc").then(({ data }) => setKycSubs(data)).catch(() => {});
  const loadRisk = () => api.get("/admin/risk-flags").then(({ data }) => setRiskFlags(data)).catch(() => {});
  const loadChat = () => api.get("/admin/chat-sessions").then(({ data }) => setChatSessions(data)).catch(() => {});
  const loadBlog = () => api.get("/blog").then(({ data }) => setBlogPosts(data)).catch(() => {});
  const loadPassports = () => api.get("/admin/gem-passports").then(({ data }) => setPassports(data)).catch(() => {});
  const loadMpAnalytics = () => api.get("/admin/marketplace/analytics").then(({ data }) => setMpAnalytics(data)).catch(() => {});

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/inquiries").then(({ data }) => setInquiries(data)).catch(() => {});
    loadKyc(); loadRisk(); loadChat(); loadBlog(); loadPassports(); loadMpAnalytics(); loadCleanupPreview();
  }, [user]);

  const reviewKyc = async (id, action) => {
    try { await api.post(`/admin/kyc/${id}/${action}`); loadKyc(); toast.success(`KYC ${action}d.`); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const generateBlog = async () => {
    setGenBusy(true);
    try {
      await api.post("/admin/blog/generate", { category: blogCategory });
      toast.success("Editorial drafted.");
      loadBlog();
    } catch (err) { toast.error(err.response?.data?.detail || "Generation failed"); }
    finally { setGenBusy(false); }
  };

  const redeemPassport = async (code) => {
    try {
      await api.post(`/gem-passport/redeem/${code}`);
      toast.success(`${code} redeemed.`);
      loadPassports();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="pt-40 text-center text-white/40">Loading…</div>;
  if (!user || user === false || user.role !== "admin") {
    return (
      <div className="pt-40 max-w-md mx-auto px-6 text-center">
        <h1 className="font-heading text-5xl mb-6">Admin Only</h1>
        <Link to="/login" data-testid="admin-login-cta" className="inline-block bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-9 py-4">Sign In</Link>
      </div>
    );
  }

  // Core admin dashboard tabs (5 internal modules) + supporting maison ops
  const tabs = [
    { k: "overview", l: "Overview" },
    { k: "analytics", l: "Analytics" },
    { k: "inventory", l: "Inventory" },
    { k: "inventory-import", l: "Import" },
    { k: "auctions", l: "Auctions" },
    { k: "certificates", l: "Certificates" },
    { k: "birthstones-zodiacs", l: "Birthstones & Zodiacs" },
    { k: "orders", l: "Orders" },
    { k: "qris-orders", l: "QRIS Orders" },
    { k: "reviews", l: "Reviews" },
    { k: "crm", l: "Customers (CRM)" },
    { k: "kyc", l: "KYC" },
    { k: "inquiries", l: "Inquiries" },
    { k: "risk", l: "Risk" },
    { k: "chat", l: "AI Chats" },
    { k: "marketing", l: "Marketing" },
    { k: "passports", l: "Passports" },
    { k: "translations", l: "Translations" },
    { k: "fx", l: "FX Rates" },
    { k: "cleanup", l: "Cleanup" },
  ];

  const passportTotal = passports.length;
  const passportRedeemed = passports.filter((p) => p.redeemed_at).length;
  const passportRate = passportTotal ? Math.round((passportRedeemed / passportTotal) * 100) : 0;

  const Stat = ({ label, value }) => (
    <div className="bg-onyx border border-white/10 p-8">
      <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">{label}</div>
      <div className="font-heading text-5xl text-ivory">{value ?? "—"}</div>
    </div>
  );

  return (
    <div data-testid="admin-page" className="pt-32">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Maison Office</p>
        <h1 className="font-heading font-light text-5xl md:text-6xl mb-10">Atelier Dashboard</h1>

        <div className="flex flex-wrap gap-3 border-b border-white/10 pb-6 mb-10 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`font-accent text-[11px] tracking-[0.28em] uppercase px-5 py-3 whitespace-nowrap transition-colors duration-500 ${tab === t.k ? "text-gold border-b border-gold -mb-[25px] pb-6" : "text-white/50 hover:text-white"}`}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Stat label="Inventory" value={stats?.gemstones} />
              <Stat label="Live Auctions" value={stats?.auctions_live} />
              <Stat label="Total Bids" value={stats?.bids} />
              <Stat label="Certificates" value={stats?.certificates} />
              <Stat label="Clients" value={stats?.users} />
              <Stat label="New Inquiries" value={stats?.inquiries_new} />
              <Stat label="Total Inquiries" value={stats?.inquiries_total} />
              <Stat label="Auctions" value={stats?.auctions_total} />
            </div>

            <div className="mt-10">
              <NotificationsPanel />
            </div>

            {/* Marketplace click analytics — Shopee / Tokopedia / TikTok Shop */}
            <div data-testid="mp-analytics-card" className="mt-10 bg-onyx border border-white/10 p-8 pb-32">
              <div className="flex items-baseline justify-between mb-6">
                <div>
                  <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">Marketplace Clicks</div>
                  <div className="font-heading text-3xl text-ivory">{mpAnalytics?.total ?? 0} outbound checkout clicks</div>
                </div>
                <div className="flex gap-6 text-sm">
                  <div className="text-right">
                    <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/40">Shopee</div>
                    <div className="font-heading text-xl text-ivory" data-testid="mp-count-shopee">{mpAnalytics?.by_platform?.shopee ?? 0}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/40">Tokopedia</div>
                    <div className="font-heading text-xl text-ivory" data-testid="mp-count-tokopedia">{mpAnalytics?.by_platform?.tokopedia ?? 0}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/40">TikTok Shop</div>
                    <div className="font-heading text-xl text-ivory" data-testid="mp-count-tiktok_shop">{mpAnalytics?.by_platform?.tiktok_shop ?? 0}</div>
                  </div>
                </div>
              </div>
              {mpAnalytics?.top_products?.length > 0 && (
                <div>
                  <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/40 mb-3">Top products</div>
                  <div className="divide-y divide-white/5 border-t border-white/5">
                    {mpAnalytics.top_products.slice(0, 8).map((row) => (
                      <div key={`${row.gemstone_id}-${row.platform}`} className="flex items-center justify-between py-2 text-sm">
                        <div className="text-white/80 truncate">{row.gemstone_name || row.gemstone_id}</div>
                        <div className="flex items-center gap-4 shrink-0">
                          <span className="font-accent text-[10px] tracking-[0.22em] uppercase text-gold">{row.platform.replace("_", " ")}</span>
                          <span className="text-ivory font-heading">{row.clicks}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!mpAnalytics?.top_products?.length && (
                <p className="text-white/40 text-sm">No marketplace clicks yet. Add Shopee / Tokopedia / TikTok Shop URLs to a product in Inventory to start tracking.</p>
              )}
            </div>
          </>
        )}

        {tab === "inventory" && <InventoryManager />}
        {tab === "inventory-import" && <InventoryImport />}
        {tab === "auctions" && <AuctionApproval />}
        {tab === "certificates" && <CertificateGenerator />}
        {tab === "birthstones-zodiacs" && <BirthstoneZodiacEditor />}
        {tab === "analytics" && <VisitorAnalytics />}
        {tab === "orders" && <OrderTracking />}
        {tab === "qris-orders" && <QrisOrders />}
        {tab === "reviews" && <ReviewsAdmin />}
        {tab === "crm" && <CustomerCRM />}

        {tab === "inquiries" && (
          <div className="space-y-4 pb-32">
            {inquiries.map((q) => (
              <div key={q.id} className="bg-onyx border border-white/10 p-6">
                <div className="flex flex-wrap justify-between gap-3 mb-3">
                  <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold">{q.type}</div>
                  <div className="text-white/40 text-xs">{new Date(q.created_at).toLocaleString()}</div>
                </div>
                <div className="font-heading text-xl">{q.name} · <span className="text-white/50 text-sm">{q.email}</span></div>
                <div className="text-white/70 mt-2">{q.message}</div>
              </div>
            ))}
            {inquiries.length === 0 && <div className="text-white/40">No inquiries.</div>}
          </div>
        )}

        {tab === "kyc" && (
          <div className="space-y-4 pb-32">
            {kycSubs.map((s) => (
              <div key={s.id} data-testid={`kyc-row-${s.id}`} className="bg-onyx border border-white/10 p-6">
                <div className="flex flex-wrap justify-between gap-3 mb-4">
                  <div>
                    <span className={`font-accent text-[10px] tracking-[0.28em] uppercase ${
                      s.status === "approved" ? "text-gold" : s.status === "rejected" ? "text-red-400" : "text-yellow-400"
                    }`}>{s.status}</span>
                    <span className="ml-3 text-white/40 text-xs">{new Date(s.submitted_at).toLocaleString()}</span>
                  </div>
                  <div className="font-heading text-xl">{s.full_name} · <span className="text-white/50 text-sm">{s.user_email}</span></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                  <div><span className="text-white/40">DOB:</span> {s.date_of_birth}</div>
                  <div><span className="text-white/40">Country:</span> {s.country}</div>
                  <div><span className="text-white/40">Nationality:</span> {s.nationality}</div>
                  <div><span className="text-white/40">ID:</span> {s.id_type} · {s.id_number}</div>
                  <div><span className="text-white/40">Occupation:</span> {s.occupation}</div>
                  <div><span className="text-white/40">Net Worth:</span> {s.net_worth_bracket}</div>
                  <div><span className="text-white/40">PEP:</span> {s.politically_exposed ? "Yes" : "No"}</div>
                  <div className="col-span-2"><span className="text-white/40">Source:</span> {s.source_of_funds}</div>
                  <div className="col-span-2"><span className="text-white/40">Address:</span> {s.residential_address}</div>
                </div>
                {s.status === "pending" && (
                  <div className="flex gap-3">
                    <button data-testid={`kyc-approve-${s.id}`} onClick={() => reviewKyc(s.id, "approve")} className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-xs px-6 py-3 hover:bg-ivory">Approve</button>
                    <button data-testid={`kyc-reject-${s.id}`} onClick={() => reviewKyc(s.id, "reject")} className="border border-red-500/50 text-red-400 font-accent uppercase tracking-[0.24em] text-xs px-6 py-3 hover:bg-red-500/10">Reject</button>
                  </div>
                )}
                {s.reviewed_by && <div className="mt-3 text-white/40 text-xs">Reviewed by {s.reviewed_by} on {new Date(s.reviewed_at).toLocaleString()}</div>}
              </div>
            ))}
            {kycSubs.length === 0 && <div className="text-white/40">No KYC submissions.</div>}
          </div>
        )}

        {tab === "risk" && (
          <div className="space-y-3 pb-32">
            <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">AI Trust & Security — Bid Risk Flags</div>
            {riskFlags.map((r) => (
              <div key={r.id} data-testid={`risk-row-${r.id}`} className={`bg-onyx border p-5 ${r.level === "high" ? "border-red-500/50" : r.level === "medium" ? "border-yellow-400/40" : "border-white/10"}`}>
                <div className="flex flex-wrap justify-between gap-3 mb-2">
                  <div className="font-accent text-[10px] tracking-[0.28em] uppercase">
                    <span className={r.level === "high" ? "text-red-400" : r.level === "medium" ? "text-yellow-400" : "text-white/60"}>{r.level} · {r.score}/100</span>
                  </div>
                  <div className="text-white/40 text-xs">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <div className="font-heading text-xl">{r.user_name} · <span className="text-white/50 text-sm">{r.user_email}</span></div>
                <div className="text-ivory mt-1">Bid: ${Intl.NumberFormat("en-US").format(r.amount)}</div>
                <div className="text-white/60 text-sm mt-2">Reasons: {r.reasons?.join(" · ")}</div>
              </div>
            ))}
            {riskFlags.length === 0 && <div className="text-white/40">No flagged bids.</div>}
          </div>
        )}

        {tab === "chat" && (
          <div className="pb-32">
            <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">AI Concierge Sessions</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-white/40 font-accent text-[10px] tracking-[0.28em] uppercase border-b border-white/10">
                  <tr><th className="text-left py-4">Session</th><th className="text-left">Client</th><th className="text-left">Messages</th><th className="text-left">Last Active</th></tr>
                </thead>
                <tbody>
                  {chatSessions.map((s) => (
                    <tr key={s.session_id} className="border-b border-white/5">
                      <td className="py-4 font-accent tracking-[0.18em] text-white/60">{s.session_id.slice(0, 12)}…</td>
                      <td>{s.user_email || <span className="text-white/40">guest</span>}</td>
                      <td className="text-ivory">{s.message_count}</td>
                      <td className="text-white/50">{new Date(s.last_active).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {chatSessions.length === 0 && <div className="text-white/40 mt-4">No conversations yet.</div>}
            </div>
          </div>
        )}

        {tab === "marketing" && (
          <div className="pb-32">
            <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">AI Marketing Director</div>
            <div className="bg-onyx border border-gold/30 p-8 mb-10">
              <div className="font-heading text-2xl mb-4">Generate today's editorial</div>
              <p className="text-white/60 text-sm mb-6 max-w-xl">Claude Sonnet 4.5 drafts a polished long-form market insight in seconds. Review and publish.</p>
              <div className="flex flex-wrap gap-4 items-center">
                <select data-testid="blog-category-select" value={blogCategory} onChange={(e) => setBlogCategory(e.target.value)} className="bg-transparent border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-gold">
                  <option value="market_insight" className="bg-noir">Market Insight</option>
                  <option value="heritage" className="bg-noir">Heritage</option>
                  <option value="auction_preview" className="bg-noir">Auction Preview</option>
                  <option value="investment" className="bg-noir">Investment</option>
                </select>
                <button data-testid="blog-generate-btn" onClick={generateBlog} disabled={genBusy} className="bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-7 py-3 hover:bg-ivory disabled:opacity-50">
                  {genBusy ? "Drafting…" : "Generate Post"}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {blogPosts.map((p) => (
                <div key={p.slug} className="bg-onyx border border-white/10 p-6 flex gap-6">
                  {p.image_url && <img src={p.image_url} alt="" className="w-32 h-32 object-cover flex-shrink-0" />}
                  <div className="flex-1">
                    <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">
                      {p.category?.replace("_", " ") || "post"} · {p.ai_generated ? "AI-drafted" : "Editorial"}
                    </div>
                    <div className="font-heading text-2xl leading-tight mb-2">{p.title}</div>
                    <div className="text-white/60 text-sm">{p.excerpt}</div>
                    <div className="text-white/40 text-xs mt-3 font-accent tracking-[0.2em] uppercase">{p.published_at ? new Date(p.published_at).toLocaleString() : ""}</div>
                  </div>
                </div>
              ))}
              {blogPosts.length === 0 && <div className="text-white/40">No posts yet — generate the first one.</div>}
            </div>
          </div>
        )}

        {tab === "passports" && (
          <div className="pb-32">
            <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Personal Gem Passports — Conversion Funnel</div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <Stat label="Issued" value={passportTotal} />
              <Stat label="Redeemed" value={passportRedeemed} />
              <Stat label="Conversion Rate" value={`${passportRate}%`} />
              <Stat label="Open" value={passportTotal - passportRedeemed} />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-white/40 font-accent text-[10px] tracking-[0.28em] uppercase border-b border-white/10">
                  <tr>
                    <th className="text-left py-4">Code</th>
                    <th className="text-left">Holder</th>
                    <th className="text-left">Primary</th>
                    <th className="text-left">Issued</th>
                    <th className="text-left">Status</th>
                    <th className="text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  {passports.map((p) => (
                    <tr key={p.id} data-testid={`passport-row-${p.claim_code}`} className="border-b border-white/5">
                      <td className="py-4 font-accent tracking-[0.18em] text-gold">{p.claim_code}</td>
                      <td>{p.holder_name || "—"}<div className="text-white/40 text-xs">{p.holder_email || ""}</div></td>
                      <td className="text-white/70 text-xs">{p.primary_id?.slice(0, 8)}…</td>
                      <td className="text-white/50">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td>
                        {p.redeemed_at ? (
                          <span className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold">Redeemed · {new Date(p.redeemed_at).toLocaleDateString()}</span>
                        ) : (
                          <span className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/50">Open</span>
                        )}
                      </td>
                      <td>
                        {!p.redeemed_at && (
                          <button data-testid={`passport-redeem-${p.claim_code}`} onClick={() => redeemPassport(p.claim_code)} className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold hover:text-ivory">
                            Mark Redeemed
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {passports.length === 0 && <div className="text-white/40 mt-4">No passports issued yet.</div>}
            </div>
          </div>
        )}

        {tab === "translations" && <TranslationsEditor />}

        {tab === "fx" && <FxOverrideEditor />}

        {tab === "cleanup" && (
          <div data-testid="admin-cleanup-pane" className="pb-32 max-w-3xl">
            <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Test Data Cleanup</div>
            <div className="bg-onyx border border-yellow-500/30 p-8 mb-10">
              <p className="text-white/70 text-sm leading-relaxed mb-4">
                This permanently deletes <span className="text-ivory">all seed / demo data</span> from the database — the 12 sample gemstones,
                the 4 sample auctions and their bids, the 4 sample certificates, plus every inquiry, wishlist item, payment transaction,
                bank order, KYC submission, AI Chat session, gem passport and consultation in the system.
              </p>
              <p className="text-white/50 text-sm leading-relaxed mb-4">
                <span className="text-ivory">Your admin account is NEVER touched.</span> Any new gemstones, auctions or certificates you
                created yourself (with names different from the seed list) will also be preserved.
              </p>
              <p className="text-white/40 text-xs leading-relaxed">
                After this runs, set <code className="text-gold">SEED_DATA=0</code> in <code className="text-gold">backend/.env</code> and
                redeploy so the demo data doesn't come back on the next container restart.
              </p>
            </div>

            {cleanup.preview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                {[
                  ["Seed Gemstones", cleanup.preview.gemstones_seed],
                  ["Seed Auctions", cleanup.preview.auctions_referencing_seed],
                  ["Seed Certificates", cleanup.preview.certificates_seed],
                  ["Bids", cleanup.preview.bids_total],
                  ["Inquiries", cleanup.preview.inquiries_total],
                  ["Wishlist Items", cleanup.preview.wishlist_items_total],
                  ["Payment Tx", cleanup.preview.payment_transactions_total],
                  ["Bank Orders", cleanup.preview.bank_orders_total],
                  ["KYC Submissions", cleanup.preview.kyc_submissions_total],
                  ["Risk Flags", cleanup.preview.risk_flags_total],
                  ["AI Chat Sessions", cleanup.preview.chat_sessions_total],
                  ["Gem Passports", cleanup.preview.gem_passports_total],
                ].map(([label, val]) => (
                  <div key={label} className="bg-onyx border border-white/10 p-5">
                    <div className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/40 mb-2">{label}</div>
                    <div className="font-heading text-3xl text-ivory">{val ?? 0}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-onyx border border-red-500/40 p-8">
              <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-red-400 mb-5">Danger Zone</div>

              <label className="flex items-center gap-3 mb-3 text-white/80 cursor-pointer">
                <input
                  data-testid="cleanup-wipe-users"
                  type="checkbox"
                  checked={cleanup.wipeUsers}
                  onChange={(e) => setCleanup({ ...cleanup, wipeUsers: e.target.checked })}
                  className="accent-gold w-4 h-4"
                />
                Also delete demo VIP + demo Client user accounts (admin always preserved)
              </label>
              <label className="flex items-center gap-3 mb-6 text-white/80 cursor-pointer">
                <input
                  data-testid="cleanup-wipe-blog"
                  type="checkbox"
                  checked={cleanup.wipeBlog}
                  onChange={(e) => setCleanup({ ...cleanup, wipeBlog: e.target.checked })}
                  className="accent-gold w-4 h-4"
                />
                Also delete all AI-generated blog posts
              </label>

              <div className="mb-5">
                <label className="block font-accent text-[10px] tracking-[0.28em] uppercase text-white/50 mb-2">
                  To confirm, type: <span className="text-red-400">DELETE TEST DATA</span>
                </label>
                <input
                  data-testid="cleanup-confirm-input"
                  value={cleanup.phrase}
                  onChange={(e) => setCleanup({ ...cleanup, phrase: e.target.value })}
                  placeholder="DELETE TEST DATA"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-red-400 font-mono tracking-wider"
                />
              </div>

              <button
                data-testid="cleanup-execute-btn"
                onClick={executeCleanup}
                disabled={cleanup.busy || cleanup.phrase.trim() !== "DELETE TEST DATA"}
                className="bg-red-500/90 text-white font-accent uppercase tracking-[0.28em] text-xs px-8 py-4 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {cleanup.busy ? "Wiping…" : "Permanently Delete Test Data"}
              </button>
            </div>

            {cleanup.result && (
              <div data-testid="cleanup-result" className="mt-8 border border-gold/40 bg-onyx p-8">
                <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Cleanup Complete</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  {Object.entries(cleanup.result.deleted || {}).map(([k, v]) => (
                    <div key={k} className="border-l border-gold/40 pl-3">
                      <span className="text-white/50">{k.replaceAll("_", " ")}:</span> <span className="text-ivory">{v}</span>
                    </div>
                  ))}
                </div>
                <p className="text-white/50 text-xs mt-6">{cleanup.result.note}</p>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default Admin;
